import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { parseErrorMessage } from "@/lib/queryClient";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { PulseDigestHealth } from "./types";
import { formatDigestSendHour } from "./helpers";

export function PulseDigestHealthPanel() {
  const healthQuery = useQuery<PulseDigestHealth>({
    queryKey: ["/api/admin/pulse/digest-health"],
    queryFn: async () => {
      const res = await fetch("/api/admin/pulse/digest-health", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load Pulse digest health");
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const data = healthQuery.data;
  const failingHosts = data?.failingHosts ?? [];

  const stats: Array<{ label: string; value: number; testid: string; tone?: string }> = data
    ? [
        { label: "Subscribers (candidates)", value: data.subscribers, testid: "subscribers" },
        { label: "Sent", value: data.sent, testid: "sent", tone: "text-green-600" },
        { label: "Failed", value: data.failed, testid: "failed", tone: data.failed > 0 ? "text-red-600" : undefined },
        { label: "No Resend", value: data.noResend, testid: "no-resend", tone: data.noResend > 0 ? "text-amber-600" : undefined },
        { label: "Never attempted", value: data.neverAttempted, testid: "never-attempted" },
      ]
    : [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold" data-testid="text-pulse-digest-title">
            Pulse Digest Health
          </h3>
          <p className="text-sm text-muted-foreground">
            Latest per-host delivery status for the daily Pulse email digest, derived from the durable
            attempt record written on every send. Use this to confirm and triage a digest-delivery
            outage. Read-only.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => healthQuery.refetch()}
          disabled={healthQuery.isFetching}
          data-testid="button-refresh-pulse-digest"
        >
          {healthQuery.isFetching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {healthQuery.isLoading ? (
        <Card className="p-6"><Skeleton className="h-20 w-full" /></Card>
      ) : healthQuery.error ? (
        <Card className="p-6">
          <p className="text-sm text-red-600" data-testid="text-pulse-digest-error">
            {parseErrorMessage(healthQuery.error, "Failed to load Pulse digest health")}
          </p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {stats.map((s) => (
              <Card key={s.testid} className="p-4" data-testid={`card-pulse-digest-${s.testid}`}>
                <div className="text-sm text-muted-foreground">{s.label}</div>
                <div
                  className={`text-2xl font-semibold tabular-nums ${s.tone ?? ""}`}
                  data-testid={`text-pulse-digest-${s.testid}`}
                >
                  {s.value.toLocaleString()}
                </div>
              </Card>
            ))}
          </div>

          <div>
            <h4 className="text-sm font-semibold mb-2" data-testid="text-pulse-digest-failing-title">
              Hosts with a failing latest attempt
            </h4>
            {failingHosts.length === 0 ? (
              <Card className="p-6">
                <p className="text-sm text-muted-foreground" data-testid="text-pulse-digest-failing-empty">
                  No hosts are currently in a failed state. 🎉
                </p>
              </Card>
            ) : (
              <Card className="p-0 overflow-hidden">
                <div className="px-4 py-2 text-sm text-muted-foreground border-b" data-testid="text-pulse-digest-failing-count">
                  {failingHosts.length.toLocaleString()} {failingHosts.length === 1 ? "host" : "hosts"}
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Host</TableHead>
                      <TableHead className="w-28">Status</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="w-28">Send hour</TableHead>
                      <TableHead className="w-44">Last attempt</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {failingHosts.map((h, i) => (
                      <TableRow key={`${h.userId}-${i}`} data-testid={`row-pulse-digest-host-${h.userId}`}>
                        <TableCell className="text-sm" data-testid={`text-pulse-digest-host-${h.userId}`}>
                          {h.name && <div className="font-medium">{h.name}</div>}
                          <div className="text-muted-foreground">{h.email ?? `User #${h.userId}`}</div>
                        </TableCell>
                        <TableCell data-testid={`text-pulse-digest-status-${h.userId}`}>
                          <Badge variant={h.status === "no_resend" ? "secondary" : "destructive"}>
                            {h.status === "no_resend" ? "No Resend" : "Failed"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm whitespace-pre-wrap break-words max-w-md" data-testid={`text-pulse-digest-reason-${h.userId}`}>
                          {h.error ?? "—"}
                        </TableCell>
                        <TableCell className="text-sm tabular-nums" data-testid={`text-pulse-digest-sendhour-${h.userId}`}>
                          {formatDigestSendHour(h.digestSendHour)}
                        </TableCell>
                        <TableCell className="text-sm tabular-nums" data-testid={`text-pulse-digest-attempt-${h.userId}`}>
                          {h.attemptAt ? new Date(h.attemptAt).toLocaleString() : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}
